import {
  Container,
  Paper,
  Text,
  Title,
  Group,
  Button,
  TextInput,
  Select,
  Table,
  ActionIcon,
  Stack,
  Loader,
  Modal,
  FileInput,
  Alert,
  List,
  Checkbox,
} from '@mantine/core';
import { IconPlus, IconSearch, IconEye, IconEdit, IconTrash, IconUpload } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useBulkImportContracts, useContracts, useDeleteContract } from '../hooks/useContracts';
import { ContractStatusBadge } from '../components/ContractStatusBadge';
import { ContractTypeBadge } from '../components/ContractTypeBadge';
import { formatCurrency } from '../../../utils/currency';
import dayjs from 'dayjs';
import { Contract, ContractImportResult, ContractStatus, ContractType } from '../../../api/contracts';

export function ContractList() {
  const navigate = useNavigate();
  const deleteContract = useDeleteContract();
  const bulkImport = useBulkImportContracts();

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [importResult, setImportResult] = useState<ContractImportResult | null>(null);

  const [filters, setFilters] = useState<{
    search: string;
    status: ContractStatus | '';
    type: ContractType | '';
    expiringSoon: boolean;
  }>({
    search: '',
    status: '',
    type: '',
    expiringSoon: false,
  });

  const { data: contracts, isLoading } = useContracts({
    search: filters.search || undefined,
    status: filters.status || undefined,
    type: filters.type || undefined,
    expiringSoon: filters.expiringSoon || undefined,
  });

  const handleFilterChange = (key: string, value: string | boolean) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this contract?')) {
      await deleteContract.mutateAsync(id);
    }
  };

  const handleOpenImportModal = () => {
    setImportFile(null);
    setDryRun(true);
    setImportResult(null);
    setImportModalOpen(true);
  };

  const handleCloseImportModal = () => {
    setImportModalOpen(false);
    setImportFile(null);
    setImportResult(null);
  };

  const handleBulkImport = async () => {
    if (!importFile) return;

    const result = await bulkImport.mutateAsync({ file: importFile, dryRun });
    setImportResult(result);

    if (!dryRun) {
      setImportFile(null);
    }
  };

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Loader />
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" mb="xl">
        <Title order={2}>Contracts</Title>
        <Group>
          <Button
            variant="light"
            onClick={() => navigate('/contracts/dashboard')}
          >
            Dashboard
          </Button>
          <Button
            variant="light"
            leftSection={<IconUpload size={16} />}
            onClick={handleOpenImportModal}
          >
            Bulk Upload
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => navigate('/contracts/new')}
          >
            New Contract
          </Button>
        </Group>
      </Group>

      {/* Filters */}
      <Paper shadow="sm" p="md" mb="xl">
        <Group gap="md">
          <TextInput
            placeholder="Search contracts..."
            leftSection={<IconSearch size={16} />}
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Status"
            data={[
              { value: '', label: 'All Status' },
              { value: 'NOT_STARTED', label: 'Not Started' },
              { value: 'DRAFT', label: 'Draft' },
              { value: 'ACTIVE', label: 'Active' },
              { value: 'EXPIRED', label: 'Expired' },
              { value: 'TERMINATED', label: 'Terminated' },
            ]}
            value={filters.status}
            onChange={(value) => handleFilterChange('status', value || '')}
            style={{ minWidth: 150 }}
          />
          <Select
            placeholder="Type"
            data={[
              { value: '', label: 'All Types' },
              { value: 'SUPPLY_OF_ITEMS', label: 'Supply of Items' },
              { value: 'SUPPLY_AND_INSTALLATION', label: 'Supply & Installation' },
              { value: 'WORKS', label: 'Works' },
            ]}
            value={filters.type}
            onChange={(value) => handleFilterChange('type', value || '')}
            style={{ minWidth: 200 }}
          />
          <Button
            variant={filters.expiringSoon ? 'filled' : 'light'}
            onClick={() => handleFilterChange('expiringSoon', !filters.expiringSoon)}
          >
            Expiring Soon
          </Button>
        </Group>
      </Paper>

      {/* Table */}
      <Paper shadow="sm" p="md">
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Contract Number</Table.Th>
              <Table.Th>Supplier</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Contract Value</Table.Th>
              <Table.Th>Consumed</Table.Th>
              <Table.Th>Remaining</Table.Th>
              <Table.Th>Start Date</Table.Th>
              <Table.Th>End Date</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {contracts && contracts.length > 0 ? (
              contracts.map((contract: Contract) => (
                <Table.Tr key={contract.id}>
                  <Table.Td>{contract.contractNumber}</Table.Td>
                  <Table.Td>{contract.supplierName}</Table.Td>
                  <Table.Td>
                    <ContractTypeBadge type={contract.type} />
                  </Table.Td>
                  <Table.Td>{formatCurrency(contract.contractValue, contract.currency || 'MVR')}</Table.Td>
                  <Table.Td>{formatCurrency(contract.consumedValue, contract.currency || 'MVR')}</Table.Td>
                  <Table.Td>{formatCurrency(contract.remainingBalance, contract.currency || 'MVR')}</Table.Td>
                  <Table.Td>
                    {contract.startDate ? dayjs(contract.startDate).format('DD MMM YYYY') : '—'}
                  </Table.Td>
                  <Table.Td>
                    {contract.endDate ? dayjs(contract.endDate).format('DD MMM YYYY') : '—'}
                  </Table.Td>
                  <Table.Td>
                    <ContractStatusBadge status={contract.status} />
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <ActionIcon
                        variant="light"
                        color="blue"
                        onClick={() => navigate(`/contracts/${contract.id}`)}
                      >
                        <IconEye size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="light"
                        color="gray"
                        onClick={() => navigate(`/contracts/${contract.id}/edit`)}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="light"
                        color="red"
                        onClick={() => handleDelete(contract.id)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            ) : (
              <Table.Tr>
                <Table.Td colSpan={10} style={{ textAlign: 'center' }}>
                  <Text c="dimmed">No contracts found</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      <Modal
        opened={importModalOpen}
        onClose={handleCloseImportModal}
        title="Bulk Upload Contracts"
        size="lg"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Upload a CSV file with columns: AGREEMENT NUMBER, DESCRIPTION, VENDOR, STATUS, START
            DATE, END DATE. Existing contract numbers are skipped. Rows without dates import as Not
            Started with a placeholder value of 1 MVR.
          </Text>

          <FileInput
            label="CSV file"
            placeholder="Select contracts.csv"
            accept=".csv,text/csv"
            value={importFile}
            onChange={setImportFile}
            clearable
          />

          <Checkbox
            label="Preview only (dry run — no records created)"
            checked={dryRun}
            onChange={(event) => {
              setDryRun(event.currentTarget.checked);
              setImportResult(null);
            }}
          />

          {importResult && (
            <Alert
              color={importResult.errors > 0 ? 'orange' : dryRun ? 'blue' : 'green'}
              title={dryRun ? 'Preview results' : 'Import results'}
            >
              <Text size="sm">
                {importResult.created} {dryRun ? 'would be created' : 'created'},{' '}
                {importResult.skipped} skipped, {importResult.errors} errors (of{' '}
                {importResult.total} rows)
              </Text>
              {importResult.rows.some((row) => row.outcome === 'error') && (
                <List size="sm" mt="xs">
                  {importResult.rows
                    .filter((row) => row.outcome === 'error')
                    .slice(0, 10)
                    .map((row) => (
                      <List.Item key={row.contractNumber}>
                        {row.contractNumber}: {row.message}
                      </List.Item>
                    ))}
                </List>
              )}
            </Alert>
          )}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={handleCloseImportModal}>
              {importResult && !dryRun ? 'Close' : 'Cancel'}
            </Button>
            <Button
              onClick={handleBulkImport}
              loading={bulkImport.isPending}
              disabled={!importFile}
              leftSection={<IconUpload size={16} />}
            >
              {dryRun ? 'Preview Import' : 'Import Contracts'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

